if (!customElements.get('card-images')) {
  class CardImages extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.card-images__items',
        pagination: '.card-images__pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        swiperSlide: 'swiper-slide',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      if (this.sliderWrapper) {
        this._sliderWrapperDefaultClass = this.sliderWrapper.className;
      }

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.mobileOnlySlider = this.dataset.mobileOnlySlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.sliderInstance = false;

      this.calcItemMinHeight();

      if (!this.enableSlider) return;

      if (this.mobileOnlySlider) {
        this.mobileMediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.handleSliderMode = this.handleSliderMode.bind(this);
        this.mobileMediaQuery.addEventListener('change', this.handleSliderMode);
        this.handleSliderMode();
        return;
      }

      this.init();
    }

    init() {
      this.initSlider();
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      /*
       * BM mobile-peek trigger. Any BM card-images section that opts into
       * the homepage pathway slider treatment (fixed 300px slide, 10px
       * gutter, autoplay, no loop) gets added here. Ring Size Guide page
       * uses the same pattern via `bm-ring-size-guide-cards`. Desktop is
       * untouched because `mobileOnlySlider` gates initialisation behind
       * the mobile matchMedia listener.
       */
      const isBmMobilePeek =
        this.mobileOnlySlider &&
        this.section &&
        (this.section.classList.contains('bm-pathway-cards') ||
          this.section.classList.contains('bm-ring-size-guide-cards'));

      const paginationEl =
        (this.section && this.section.querySelector(this.selectors.pagination)) ||
        this.querySelector(this.selectors.pagination);
      const nextEl = this.section ? this.section.querySelector(this.selectors.nextEl) : null;
      const prevEl = this.section ? this.section.querySelector(this.selectors.prevEl) : null;

      /*
       * IMPORTANT: do NOT spread `navigation: undefined` / `pagination: false`
       * into sliderOptions. Swiper's Navigation and Pagination modules are
       * always registered by FoxTheme's default Carousel (see theme.js), and
       * their onInit hooks read `swiper.params.navigation.enabled` /
       * `swiper.params.pagination.enabled`. When we pass `undefined` or
       * `false`, Swiper's internal param merge leaves those as non-object
       * values and the onInit hook throws
       * `Cannot read properties of undefined (reading 'enabled')` — which
       * aborts Swiper construction entirely (observed on BM homepage Image
       * Card). promise-cards.js works because it omits `navigation` and only
       * conditionally includes `pagination`. We do the same here.
       */
      const sliderOptions = {
        slidesPerView: 1,
        spaceBetween: 0,
        breakpoints: {
          1024: {
            slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
            spaceBetween: columnGap.tabletLarge,
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop,
          },
        },
        loop: true,
        threshold: 2,
      };
      if (nextEl && prevEl) {
        sliderOptions.navigation = { nextEl, prevEl };
      }
      if (paginationEl) {
        sliderOptions.pagination = { el: paginationEl, clickable: true };
      }

      let extraModules = null;
      if (isBmMobilePeek) {
        sliderOptions.slidesPerView = 'auto';
        sliderOptions.spaceBetween = 10;
        /*
         * `centeredSlides: true` mirrors the Featured Collection mobile peek:
         * the active card sits centred in the viewport with equal left/right
         * peek of its neighbours. Defensive breakpoint overrides set
         * `centeredSlides: false` above 768px so if this slider is ever kept
         * alive through a viewport resize (handleSliderMode destroys it at
         * ≥768 as a belt-and-braces) the grid-style strip is never centred.
         */
        sliderOptions.centeredSlides = true;
        sliderOptions.breakpoints = {
          768: { slidesPerView: 'auto', spaceBetween: 10, centeredSlides: false },
          1024: { slidesPerView: 'auto', spaceBetween: 10, centeredSlides: false },
          1280: { slidesPerView: 'auto', spaceBetween: 10, centeredSlides: false },
        };
        sliderOptions.autoplay = {
          delay: 4000,
          disableOnInteraction: false,
        };
        extraModules = [FoxTheme.Swiper.Autoplay];
        /* Loop clones + leftover `md:f-grid-*` on the wrapper = a fake second row on wide viewports. */
        sliderOptions.loop = false;

        /*
         * BM homepage `.card-images__pagination` was rendering as an empty <div>
         * even though Swiper's Pagination module is enabled. DevTools confirmed
         * Swiper initialised the container but the pagination element never
         * received Swiper's identity classes (`swiper-pagination-bullets` /
         * `swiper-pagination-custom`), meaning the Pagination module silently
         * skipped this element — likely due to an interaction with the BM
         * section shell (motion-element wrap, `overflow: hidden` on the outer
         * `.card-images`, or Sleek's pre-stamped `swiper-pagination-bullets-animate`
         * class on the pre-existing div).
         *
         * We leave Swiper's pagination options untouched so its Pagination
         * module doesn't crash during init (passing `false` triggers
         * `Cannot read properties of undefined (reading 'enabled')` from the
         * module's onInit hook, which aborts the entire Swiper init). Instead,
         * our manual renderer below takes over after init — it produces the
         * same `<span class="swiper-pagination-bullet …">` markup Swiper would
         * have and rebinds click/keyboard handlers, so if the module fails
         * silently (as observed) we still get a functional bar, and if it
         * ever succeeds the manual renderer's output is an idempotent
         * overwrite on the first `slideChange`. Desktop untouched.
         */
      }

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        /* Always strip: `md:f-grid-*` on the wrapper beats Swiper flex at md+ and wraps loop slides. */
        FoxTheme.utils.stripTailwindGridUtilities(this.sliderWrapper);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderWrapper.querySelectorAll('.f-column').forEach((column) => {
          column.classList.add(this.classes.swiperSlide);
        });
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, extraModules);
        this.sliderInstance.init();

        /*
         * Manual pagination renderer for BM mobile-peek. Swiper's Pagination
         * module still receives `{ el, clickable: true }` above (required to
         * avoid the `enabled` crash), but it was observed to silently no-op
         * for `.bm-pathway-cards` — the pagination div never received the
         * identity classes (`swiper-pagination-bullets`) nor any child spans.
         *
         * So we render our own `<span class="swiper-pagination-bullet …">`
         * markup here: same class names as Swiper would produce, so the
         * existing CSS in `bm-overrides.css` (40px idle / 110px active with
         * the `bm-bullet-fill` 4s animation) styles them without change.
         * If Swiper's module ever does populate, our renderer's subsequent
         * `slideChange` writes are idempotent overwrites.
         *
         * - Runs on init, `slideChange`, and `update` so the active bullet
         *   stays synced with autoplay / user swipe.
         * - Click + Enter/Space delegation on `paginationEl` survives the
         *   innerHTML wipe on each re-render.
         */
        if (isBmMobilePeek && paginationEl && this.sliderInstance && this.sliderInstance.slider) {
          const swiper = this.sliderInstance.slider;

          const renderBmPagination = () => {
            const total = swiper.slides ? swiper.slides.length : 0;
            if (!total) {
              paginationEl.innerHTML = '';
              return;
            }
            const activeIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex || 0;
            let html = '';
            for (let i = 0; i < total; i += 1) {
              const isActive = i === activeIndex;
              const classes = isActive
                ? 'swiper-pagination-bullet swiper-pagination-bullet-active swiper-pagination-bullet-active-main'
                : 'swiper-pagination-bullet';
              html += `<span class="${classes}" data-bullet-index="${i}" role="button" tabindex="0" aria-label="Go to slide ${i + 1}"${isActive ? ' aria-current="true"' : ''}></span>`;
            }
            paginationEl.innerHTML = html;
          };

          renderBmPagination();
          swiper.on('slideChange', renderBmPagination);
          swiper.on('update', renderBmPagination);

          const handleBulletActivation = (event) => {
            const bullet = event.target.closest('.swiper-pagination-bullet');
            if (!bullet || !paginationEl.contains(bullet)) return;
            const index = parseInt(bullet.dataset.bulletIndex, 10);
            if (Number.isNaN(index)) return;
            event.preventDefault();
            try {
              swiper.slideTo(index);
            } catch (err) {
              console.error('[card-images] slideTo from manual pagination failed', {
                section_id: this.sectionId,
                index,
                error: err,
              });
            }
          };
          const handleBulletKeydown = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleBulletActivation(event);
            }
          };
          paginationEl.addEventListener('click', handleBulletActivation);
          paginationEl.addEventListener('keydown', handleBulletKeydown);

          this._bmPaginationEl = paginationEl;
          this._bmPaginationClickHandler = handleBulletActivation;
          this._bmPaginationKeydownHandler = handleBulletKeydown;
        }

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      if (this._bmPaginationEl) {
        this._bmPaginationEl.removeEventListener('click', this._bmPaginationClickHandler);
        this._bmPaginationEl.removeEventListener('keydown', this._bmPaginationKeydownHandler);
        this._bmPaginationEl = null;
        this._bmPaginationClickHandler = null;
        this._bmPaginationKeydownHandler = null;
      }
      this.classList.remove(this.classes.swiper);
      if (typeof this.sliderInstance === 'object') {
        try {
          this.sliderInstance.slider.destroy(true, true);
        } catch (e) {
          try {
            this.sliderInstance.slider.destroy();
          } catch (e2) {
            console.error('[card-images] Swiper destroy failed', {
              section_id: this.sectionId,
              error: e2,
              first_error: e,
            });
          }
        }
        this.sliderInstance = false;
      }
      this.sliderWrapper.querySelectorAll('.f-column').forEach((column) => {
        column.classList.remove(this.classes.swiperSlide);
      });
      if (this._sliderWrapperDefaultClass) {
        this.sliderWrapper.className = this._sliderWrapperDefaultClass;
      } else {
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
      }
    }

    handleSliderMode() {
      if (!this.mobileMediaQuery) return;

      if (this.mobileMediaQuery.matches) {
        this.init();
      } else {
        this.destroySlider();
      }
    }

    /**
     * Make sure overlay content display properly without overlap image.
     */
    calcItemMinHeight() {
      let maxHeight = 0;
      const cardMedias = this.querySelectorAll('.card-media');
      cardMedias &&
        cardMedias.forEach((cardMedia) => {
          const cardContent = cardMedia.querySelector('.card-media__content');
          if (!cardContent) return;

          const cardContentInner = cardContent.querySelector('.card-media__content-wrapper');
          const cardContentPadding = this.getElementPadding(cardContent);
          let itemHeight = cardContentInner.offsetHeight;

          if (cardContentPadding) {
            itemHeight += cardContentPadding.top + cardContentPadding.bottom;
          }

          if (itemHeight > maxHeight) {
            maxHeight = itemHeight;
          }

          cardMedia.style.setProperty('--item-height', itemHeight + 'px');
        });

      this.style.setProperty('--items-height', maxHeight + 'px');
    }

    getElementPadding(element) {
      if (!element) {
        return null;
      }

      const style = window.getComputedStyle(element);
      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);

      return {
        top: paddingTop,
        bottom: paddingBottom,
      };
    }

    disconnectedCallback() {
      if (this.mobileMediaQuery && this.handleSliderMode) {
        this.mobileMediaQuery.removeEventListener('change', this.handleSliderMode);
      }
      this.destroySlider();
    }
  }
  customElements.define('card-images', CardImages);
}
